/**
 * BezierUtils - Bezier Curve Calculations
 * Utility functions for cubic Bezier curve calculations
 */

/**
 * Evaluate cubic Bezier curve at parameter t
 * This is the standard De Casteljau algorithm
 * @param {number} p0 - Start point coordinate
 * @param {number} p1 - First control point coordinate
 * @param {number} p2 - Second control point coordinate
 * @param {number} p3 - End point coordinate
 * @param {number} t - Parameter (0-1)
 * @returns {number} Coordinate at t
 */
export function bezierPoint(p0, p1, p2, p3, t) {
    const t1 = 1 - t;
    return (
        t1 * t1 * t1 * p0 +
        3 * t1 * t1 * t * p1 +
        3 * t1 * t * t * p2 +
        t * t * t * p3
    );
}

/**
 * Evaluate cubic Bezier curve derivative (tangent) at parameter t
 * Used for calculating perpendiculars and normals
 * @param {number} p0 - Start point coordinate
 * @param {number} p1 - First control point coordinate
 * @param {number} p2 - Second control point coordinate
 * @param {number} p3 - End point coordinate
 * @param {number} t - Parameter (0-1)
 * @returns {number} Derivative at t
 */
export function bezierTangent(p0, p1, p2, p3, t) {
    const t1 = 1 - t;
    return (
        3 * t1 * t1 * (p1 - p0) +
        6 * t1 * t * (p2 - p1) +
        3 * t * t * (p3 - p2)
    );
}

/**
 * Approximate length of cubic Bezier curve segment
 * Uses adaptive subdivision for accuracy
 * @param {number} x0 - Start X
 * @param {number} y0 - Start Y
 * @param {number} x1 - Control point 1 X
 * @param {number} y1 - Control point 1 Y
 * @param {number} x2 - Control point 2 X
 * @param {number} y2 - Control point 2 Y
 * @param {number} x3 - End X
 * @param {number} y3 - End Y
 * @param {number} [steps=20] - Number of subdivision steps
 * @returns {number} Approximate length
 */
export function bezierLength(x0, y0, x1, y1, x2, y2, x3, y3, steps = 20) {
    let length = 0;
    let prevX = x0;
    let prevY = y0;

    const dt = 1.0 / steps;
    for (let t = dt; t <= 1.0; t += dt) {
        const x = bezierPoint(x0, x1, x2, x3, t);
        const y = bezierPoint(y0, y1, y2, y3, t);

        const dx = x - prevX;
        const dy = y - prevY;
        length += Math.sqrt(dx * dx + dy * dy);

        prevX = x;
        prevY = y;
    }

    return length;
}

/**
 * Split a cubic Bezier curve at parameter t
 * Returns two new Bezier curves
 * @param {number} x0 - Start X
 * @param {number} y0 - Start Y
 * @param {number} x1 - Control point 1 X
 * @param {number} y1 - Control point 1 Y
 * @param {number} x2 - Control point 2 X
 * @param {number} y2 - Control point 2 Y
 * @param {number} x3 - End X
 * @param {number} y3 - End Y
 * @param {number} t - Parameter to split at (0-1)
 * @returns {Array} [curve1, curve2] where each curve is [x0, y0, x1, y1, x2, y2, x3, y3]
 */
export function bezierSplit(x0, y0, x1, y1, x2, y2, x3, y3, t) {
    // De Casteljau subdivision
    const ax = x0 + (x1 - x0) * t;
    const ay = y0 + (y1 - y0) * t;
    const bx = x1 + (x2 - x1) * t;
    const by = y1 + (y2 - y1) * t;
    const cx = x2 + (x3 - x2) * t;
    const cy = y2 + (y3 - y2) * t;

    const dx = ax + (bx - ax) * t;
    const dy = ay + (by - ay) * t;
    const ex = bx + (cx - bx) * t;
    const ey = by + (cy - by) * t;

    const fx = dx + (ex - dx) * t;
    const fy = dy + (ey - dy) * t;

    const curve1 = [x0, y0, ax, ay, dx, dy, fx, fy];
    const curve2 = [fx, fy, ex, ey, cx, cy, x3, y3];

    return [curve1, curve2];
}
